import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  evaluateRiskForecast,
  evaluateRiskPipelineCoverage,
  type ForecastTimePeriod,
  formatCurrencyValue,
  getForecastTimePeriodRange,
  getRiskBucket,
  prepareDealsForRiskAssessment,
} from "../lib/risk";
import { getCompanyTarget } from "../lib/companies";
import { useCanonicalForecast } from "../expected-close-v2/useCanonicalForecast";
import { useAppStore } from "@/stores";
import useSWR from "swr";
import {
  getAll,
  getDeals,
  getDealstageAvgTimesByCompanyId,
} from "@/lib/supabase";
import {
  endOfMonth,
  addDays,
  differenceInCalendarMonths,
  isWithinInterval,
  // isWithinInterval,
  startOfMonth,
  startOfQuarter,
  subDays,
  subQuarters,
  format,
} from "date-fns";
import { getAPI } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { RiskHighlightCard } from "../components/RiskHighlightCard";
import { SectionCard } from "../components/SectionCard";
import {
  PipelineCreatedChart,
  ForecastDriverChart,
  HowWeWinAndLoseChart,
  PipelineMovementChart,
  RevenueVsPlanChart,
  WonByCategoryChart,
} from "../components/charts";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { usePostHog } from "@posthog/react";
import { usePageTimeTracking } from "@/lib/posthog/usePageTimeTracking";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TODO } from "@revelate/types";
import { buildManagementReviewInsightBlocks } from "./insights";
import { InsightSourceTooltip } from "../components/InsightSourceTooltip";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { DealUrgencyGauge } from "../components/DealUrgencyGauge";
import {
  RevenueIntelligenceChatDealFact,
  RevenueIntelligenceChatFacts,
  RevenueIntelligenceChatStageSummary,
} from "../components/RevenueIntelligenceChatContext";
import { RevenueIntelligenceChatRegistration } from "../components/RevenueIntelligenceChatRegistration";
import { defineRevenueIntelligenceAnalysisContract } from "../lib/revenue-intelligence-analysis-contract";
import { useLocation } from "wouter";
import {
  getRevenueIntelligencePeriod,
  persistRevenueIntelligencePeriod,
  REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT,
} from "../lib/revenue-intelligence-routes";
// Important dates
const now = new Date();
const startOf2QuartersAgo = subQuarters(startOfQuarter(now), 2);

type ManagementReviewPeriod = ForecastTimePeriod;
type WinLossWindow = "selected_period" | "past_90_days";
const showPipelineMovementWip = false;

const managementReviewPeriodOptions: Array<{ label: string; value: ManagementReviewPeriod }> = [
  { label: "Last quarter", value: "prev_quarter" },
  { label: "This quarter", value: "this_quarter" },
  { label: "Next quarter", value: "next_quarter" },
  { label: "Next 6 months", value: "next_6_months" },
  { label: "This year", value: "this_year" },
];

function isManagementReviewPeriod(value: string): value is ManagementReviewPeriod {
  return managementReviewPeriodOptions.some(option => option.value === value);
}

function getManagementReviewPeriodLabel(period: ManagementReviewPeriod) {
  return managementReviewPeriodOptions.find(option => option.value === period)?.label
    ?? "Next 6 months";
}

function getManagementReviewPeriodPhrase(period: ManagementReviewPeriod) {
  if (period === "next_6_months") return "the next 6 months";
  return getManagementReviewPeriodLabel(period).toLowerCase();
}

function getManagementReviewPeriodRange(period: ManagementReviewPeriod) {
  const range = getForecastTimePeriodRange(period, now);
  return { from: range.start, to: range.end };
}

function getUniqueDeals(deals: TODO[]) {
  return [...new Map(deals.filter(Boolean).map(deal => [deal.id, deal])).values()];
}

function sortDealsByValueDesc(deals: TODO[]) {
  return deals.toSorted((a, b) => (b.value ?? 0) - (a.value ?? 0));
}

function getInsightToneClassName(tone: "positive" | "risk" | "neutral") {
  switch (tone) {
    case "positive": return "border-green-200 bg-green-50/60";
    case "risk": return "border-red-200 bg-red-50/60";
    default: return "border-gray-200 bg-gray-50";
  }
}

function getDealRiskLabel(deal: TODO) {
  if (deal.is_won) return "won";
  if (deal.is_lost) return "lost";
  if (typeof deal.weighted_risk_score !== "number") return undefined;
  return `${getRiskBucket(deal.weighted_risk_score)} risk`;
}

function getDealStatusLabel(deal: TODO) {
  if (deal.is_won) return "won";
  if (deal.is_lost) return "lost";
  if (deal.is_closed) return "closed";
  return "open";
}

function getManagementReviewDealPeriodDate(deal: TODO) {
  return deal.is_closed
    ? deal.closed_at
    : deal.adjustedCloseDate;
}

function formatManagementReviewDealPeriodDate(deal: TODO) {
  const periodDate = getManagementReviewDealPeriodDate(deal);
  return periodDate ? format(new Date(periodDate), "yyyy-MM-dd") : "date not set";
}

function isDealInManagementReviewPeriod(
  deal: TODO,
  periodRange: { from: Date; to: Date },
) {
  const periodDate = getManagementReviewDealPeriodDate(deal);
  if (!periodDate) return false;

  return isWithinInterval(new Date(periodDate), {
    start: periodRange.from,
    end: periodRange.to,
  });
}

function formatDealFact(
  deal: TODO,
  companyCurrency: TODO,
): RevenueIntelligenceChatDealFact {
  return {
    dealId: deal.id,
    name: deal.name,
    value: deal.value,
    valueLabel: formatCurrencyValue(deal.value, companyCurrency, 1),
    statusLabel: getDealStatusLabel(deal),
    stageLabel: deal.stage_label,
    expectedCloseDateLabel: deal.adjustedCloseDate
      ? format(deal.adjustedCloseDate, "yyyy-MM-dd")
      : undefined,
    closedDateLabel: deal.closed_at
      ? format(new Date(deal.closed_at), "yyyy-MM-dd")
      : undefined,
    riskLabel: getDealRiskLabel(deal),
  };
}

function sumDealValue(deals: TODO[]) {
  return deals.reduce((sum, deal) => sum + deal.value, 0);
}

function getDealExpectedContribution(deal: TODO) {
  if (deal.is_won) return deal.value;
  if (deal.is_closed) return 0;
  return deal.value * (deal.stage_probability_adjusted ?? 0);
}

function sumExpectedContribution(deals: TODO[]) {
  return deals.reduce((sum, deal) => sum + getDealExpectedContribution(deal), 0);
}

function getForecastChartValueForMonth(chartData: TODO[], date: Date) {
  const monthKey = format(date, "yyyy-MM");
  return chartData.find(row => row.month === monthKey)?.total_minus_risk ?? 0;
}

function getForecastChartValueForPeriod(chartData: TODO[], periodRange: { from: Date; to: Date }) {
  return chartData.reduce((sum, row) => {
    const rowDate = new Date(`${row.month}-01T00:00:00`);
    if (!isWithinInterval(rowDate, { start: periodRange.from, end: periodRange.to })) {
      return sum;
    }

    return sum + (row.total_minus_risk ?? 0);
  }, 0);
}

function getTargetContribution(deal: TODO) {
  if (deal.is_won) return deal.value;
  if (deal.is_closed) return 0;

  const riskFactor = typeof deal.risk_score === "number"
    ? (100 - deal.risk_score) / 100
    : 1;

  return deal.value * (deal.stage_probability_adjusted ?? 0) * riskFactor;
}

function sumTargetContribution(deals: TODO[]) {
  return deals.reduce((sum, deal) => sum + getTargetContribution(deal), 0);
}

function getPeriodTarget(
  monthlyCompanyTarget: number,
  periodRange: { from: Date; to: Date },
) {
  const months = differenceInCalendarMonths(periodRange.to, periodRange.from) + 1;
  return monthlyCompanyTarget > 0 ? monthlyCompanyTarget * months : 0;
}

function getExpectedToCloseDeals(
  deals: TODO[],
  periodRange: { from: Date; to: Date },
  includeOpenDeals = true,
) {
  return deals
    .filter(deal => deal.value > 0)
    .filter(deal => {
      if (deal.is_won) {
        return Boolean(deal.closed_at) && isWithinInterval(new Date(deal.closed_at), {
          start: periodRange.from,
          end: periodRange.to,
        });
      }

      if (!includeOpenDeals) return false;
      if (deal.is_closed || deal.is_lost) return false;

      return Boolean(deal.adjustedCloseDate) && isWithinInterval(new Date(deal.adjustedCloseDate), {
        start: periodRange.from,
        end: periodRange.to,
      });
    })
    .toSorted((a, b) => {
      const aDate = new Date(a.is_won ? a.closed_at : a.adjustedCloseDate).getTime();
      const bDate = new Date(b.is_won ? b.closed_at : b.adjustedCloseDate).getTime();
      const dateDiff = aDate - bDate;
      return dateDiff || b.value - a.value;
    });
}

function buildStageSummary(
  deals: TODO[],
  companyCurrency: TODO,
): RevenueIntelligenceChatStageSummary[] {
  const stageMap = new Map<string, { count: number; totalValue: number }>();

  deals.forEach(deal => {
    const stageLabel = deal.stage_label ?? "Unknown stage";
    const current = stageMap.get(stageLabel) ?? { count: 0, totalValue: 0 };
    stageMap.set(stageLabel, {
      count: current.count + 1,
      totalValue: current.totalValue + deal.value,
    });
  });

  return [...stageMap.entries()]
    .map(([stageLabel, summary]) => ({
      stageLabel,
      count: summary.count,
      totalValue: summary.totalValue,
      totalValueLabel: formatCurrencyValue(summary.totalValue, companyCurrency, 1),
    }))
    .toSorted((a, b) => b.count - a.count);
}

function ManagementReviewDealListDialog({
  deals,
  companyCurrency,
  title,
  description,
  open,
  onOpenChange,
}: {
  deals: TODO[];
  companyCurrency: TODO;
  title: string;
  description: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogDescription className="max-h-[70vh] overflow-y-auto">
          <AtRiskDealsDataTable
            deals={sortDealsByValueDesc(deals)}
            showTitle={false}
            showForecastImpact
            forecastValueDisplay="contribution"
            companyCurrency={companyCurrency}
          />
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
}

export function RevenueIntelligenceManagementReviewPage() {
  const [, setLocation] = useLocation();
  const [pipelineCreatedBreakdown, setPipelineCreatedBreakdown] = useState<"source" | "segment">("source");
  const [wonBreakdown, setWonBreakdown] = useState<"source" | "segment">("source");
  const [managementReviewPeriod, setManagementReviewPeriod] = useState<ManagementReviewPeriod>(() => {
    if (typeof window === "undefined") return "next_6_months";
    const period = getRevenueIntelligencePeriod(window.location.href);
    return isManagementReviewPeriod(period) ? period : "next_6_months";
  });
  const [winLossWindow, setWinLossWindow] = useState<WinLossWindow>("selected_period");
  const [showClosedTopDeals, setShowClosedTopDeals] = useState(false);
  const [keyInsightDealList, setKeyInsightDealList] = useState<"month" | "quarter" | null>(null);

  const managementReviewPeriodRange = getManagementReviewPeriodRange(managementReviewPeriod);
  const forecastTimePeriod = managementReviewPeriod;
  const canonicalForecast = useCanonicalForecast(forecastTimePeriod);
  const isLastQuarterView = managementReviewPeriod === "prev_quarter";
  const isNextQuarterView = managementReviewPeriod === "next_quarter";
  const managementReviewPeriodPhrase = getManagementReviewPeriodPhrase(managementReviewPeriod);
  const winLossPeriodRange = winLossWindow === "past_90_days"
    ? {
      from: subDays(now, 90),
      to: now,
    }
    : managementReviewPeriodRange;

  const { currentUser, companyCurrency, setDateRange } = useAppStore((state) => state);

  const posthog = usePostHog();
  usePageTimeTracking("management_review", posthog);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("period", managementReviewPeriod);
    persistRevenueIntelligencePeriod(managementReviewPeriod);
    setLocation(`${window.location.pathname}?${params.toString()}`, { replace: true });
    window.dispatchEvent(new Event(REVENUE_INTELLIGENCE_FILTER_CONTEXT_CHANGED_EVENT));
  }, [managementReviewPeriod, setLocation]);

  function handleManagementReviewPeriodChange(value: ManagementReviewPeriod) {
    const nextPeriodRange = getManagementReviewPeriodRange(value);
    setManagementReviewPeriod(value);
    setWinLossWindow(value === "next_quarter" ? "past_90_days" : "selected_period");
    setDateRange({
      from: nextPeriodRange.from,
      to: nextPeriodRange.to,
    });
  }

  const {
    data: allDealsInManagementPeriod,
    // error: wonDealsThisQuarterError,
    isLoading: isLoadingManagementReviewDeals,
  } = useSWR(
    [
      'query:managementReviewDealsInPeriod',
      currentUser ? currentUser?.company_id.toString() : null,
      managementReviewPeriodRange.from.toISOString(),
      managementReviewPeriodRange.to.toISOString(),
    ],
    ([, companyId, from, to]) => getDeals(companyId, {
      dateRange: {
        from,
        to,
      },
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: allOpenDeals,
    // error: allOpenDealsError,
    isLoading: isLoadingAllOpenDeals,
  } = useSWR(
    [
      'query:allOpenDeals',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([, companyId]) => getDeals(companyId, {
      onlyOpen: true,
      nonZeroValue: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: winLossReferenceDeals,
    isLoading: isLoadingWinLossReferenceDeals,
  } = useSWR(
    [
      'query:managementReviewWinLossReferenceDeals',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([, companyId]) => getDeals(companyId, {
      dateRange: {
        from: subDays(now, 90).toISOString(),
        to: now.toISOString(),
      },
      nonZeroValue: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // const {
  //   data: newDealsCreatedThisQuarter,
  //   // error: wonDealsThisQuarterError,
  //   // isLoading: isLoadingWonDealsThisQuarter,
  // } = useSWR(
  //   [
  //     'query:newDealsCreatedThisQuarter',
  //     currentUser ? currentUser?.company_id.toString() : null,
  //   ],
  //   ([_, companyId]) => getDeals(companyId, {
  //     dateRange: {
  //       from: startOfThisQuarter.toISOString(),
  //       to: endOfThisQuarter.toISOString(),
  //     },
  //     filterByCreateDateInsteadOfCloseDate: true,
  //   }),
  //   {
  //     revalidateOnFocus: false,
  //     revalidateOnReconnect: false,
  //   }
  // );

  const {
    data: dealsForForecast,
    // error: dealsForForecastError,
    isLoading: isLoadingDealsForForecast,
  } = useSWR(
    [
      'query:dealsForForecast',
      currentUser ? currentUser?.company_id.toString() : null,
      startOf2QuartersAgo.toISOString(),
      managementReviewPeriodRange.to.toISOString(),
    ],
    ([, companyId, from, to]) => getDeals(companyId, {
      dateRange: { from, to },
      nonZeroValue: true,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: currencies,
    // error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useSWR(
    [
      'query:currencies',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([, companyId]) => getAll("currencies", companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: pipelines,
    // error: pipelinesError,
  } = useSWR(
    [
      'query:pipelines',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([, companyId]) => companyId
      ? getAPI(`providers/hubspot/pipelines/${companyId}`)
      : [],
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  const {
    data: dealstageAvgTimes,
    // error: dealstageAvgTimesError,
    isLoading: isLoadingDealstageAvgTimes,
  } = useSWR(
    [
      'query:dealstageAvgTimes',
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([, companyId]) => companyId
      ? getDealstageAvgTimesByCompanyId(parseInt(companyId, 10))
      : [],
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  if (canonicalForecast.error) return <div role="alert" className="p-8 text-center">
    <p>Forecast data could not be loaded.</p>
    <button onClick={() => void canonicalForecast.retry()}>Try again</button>
  </div>;

  if (
    canonicalForecast.isLoading ||
    !canonicalForecast.position ||
    isLoadingManagementReviewDeals ||
    isLoadingAllOpenDeals ||
    isLoadingWinLossReferenceDeals ||
    isLoadingDealsForForecast ||
    isLoadingCurrencies ||
    isLoadingDealstageAvgTimes ||
    !allDealsInManagementPeriod ||
    !allOpenDeals ||
    !winLossReferenceDeals ||
    !dealsForForecast ||
    !currencies ||
    !dealstageAvgTimes
  ) return (
    <div className="flex h-[70vh] flex-col items-center justify-center gap-3 text-center">
      <Loader parentClassName="h-auto scale-100" size={34} stroke="#231942" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-[#231942]">
          Running revenue intelligence
        </p>
        <p className="text-xs text-gray-500">
          Preparing signals, forecast and deal context.
        </p>
      </div>
    </div>
  );

  const hasPipelineStageData = Boolean(
    pipelines?.results?.length &&
    dealstageAvgTimes?.length
  );

  // Keep protected period metrics on the original period dataset; use the merged
  // dataset only for new open-deal workflows such as chat, urgency and drill-downs.
  const hubspotDealsForPipelineCoverage = prepareDealsForRiskAssessment(
    allDealsInManagementPeriod,
    pipelines,
    currencies as Parameters<typeof prepareDealsForRiskAssessment>[2],
    currentUser?.company as unknown as Parameters<typeof prepareDealsForRiskAssessment>[3],
    dealstageAvgTimes,
  );

  const managementReviewDeals = getUniqueDeals([
    ...(allDealsInManagementPeriod ?? []),
    ...(allOpenDeals ?? []),
  ]);

  const hubspotManagementReviewDeals = prepareDealsForRiskAssessment(
    managementReviewDeals,
    pipelines,
    currencies as Parameters<typeof prepareDealsForRiskAssessment>[2],
    currentUser?.company as unknown as Parameters<typeof prepareDealsForRiskAssessment>[3],
    dealstageAvgTimes,
  );

  const managementReviewWinLossDeals = getUniqueDeals([
    ...(allDealsInManagementPeriod ?? []),
    ...(winLossReferenceDeals ?? []),
  ]);

  const hubspotManagementReviewWinLossDeals = prepareDealsForRiskAssessment(
    managementReviewWinLossDeals,
    pipelines,
    currencies as Parameters<typeof prepareDealsForRiskAssessment>[2],
    currentUser?.company as unknown as Parameters<typeof prepareDealsForRiskAssessment>[3],
    dealstageAvgTimes,
  );

  const wonDealsThisQuarterForPipelineCoverage = hubspotDealsForPipelineCoverage
    ?.filter(d => d.is_won);

  const closedOutcomeDealsInPeriod = (hubspotDealsForPipelineCoverage ?? [])
    .filter(deal => deal.is_won || deal.is_lost);

  const winRateThisQuarter = isNextQuarterView || !closedOutcomeDealsInPeriod.length
    ? null
    : 100 * closedOutcomeDealsInPeriod.filter(deal => deal.is_won).length / closedOutcomeDealsInPeriod.length;

  const totalClosedOutcomeDealSizeThisQuarter = closedOutcomeDealsInPeriod
    .map(d => d.value)
    .reduce((acc, val) => acc + val, 0);

  const avgDealSizeThisQuarter = !closedOutcomeDealsInPeriod.length
    ? null
    : totalClosedOutcomeDealSizeThisQuarter / closedOutcomeDealsInPeriod.length;


  // const newDealsThisQuarterForPipelineCoverage = prepareDealsWithCurrencyConversion(
  //   newDealsCreatedThisQuarter,
  //   // @ts-ignore
  //   currencies,
  //   currentUser?.company,
  // );
  //
  // const pipelineCreatedThisQuarter = newDealsThisQuarterForPipelineCoverage
  //   ?.filter(d => {
  //     const createdate = d.provider_data?.properties?.createdate;
  //     if (!createdate) return false;
  //     return isWithinInterval(createdate, {
  //       start: startOfThisQuarter,
  //       end: endOfThisQuarter,
  //     })
  //   })
  //   ?.map(d => d.value)
  //   ?.reduce((acc, val) => acc + val, 0);

  const hubspotDealsForForecast = prepareDealsForRiskAssessment(
    dealsForForecast,
    pipelines,
    currencies as Parameters<typeof prepareDealsForRiskAssessment>[2],
    currentUser?.company as unknown as Parameters<typeof prepareDealsForRiskAssessment>[3],
    dealstageAvgTimes,
  );

  const companyTarget = getCompanyTarget(currentUser?.company_id ?? 0);

  const {
    forecastWaterfallChartData: chartDataWaterfall,
  } = hasPipelineStageData
    ? evaluateRiskForecast(
      hubspotDealsForForecast,
      currentUser?.company_id,
      pipelines?.results ?? [],
      forecastTimePeriod, // forecastTimePeriod
      "all", // globalSelectedPipeline
      "all", //globalSelectedOwner
      companyTarget,
      false, //isForecastAccumulated
      companyCurrency,
    )
    : {
      forecastWaterfallChartData: [],
    };
  const chartDataForecast = canonicalForecast.chartData;

  const {
    isEarlyStageWeak,
    isMidStageWeak,
    isLateStageWeak,
    managementReviewUrgencyRisks,
  } = evaluateRiskPipelineCoverage(
    hubspotManagementReviewDeals ?? [],
    currentUser?.company_id,
    wonDealsThisQuarterForPipelineCoverage ?? [],
    pipelines?.results ?? [],
    companyTarget,
    "all", // globalSelectedPipeline,
    "all", //globalSelectedOwner,
    pipelineCreatedBreakdown,
    wonBreakdown,
    managementReviewPeriodRange,
  );

  const {
    pipelineCreatedByCategory,
    attainmentByCategory: wonByCategory,
    managementReviewPipelineMovement,
  } = evaluateRiskPipelineCoverage(
    hubspotDealsForPipelineCoverage ?? [],
    currentUser?.company_id,
    wonDealsThisQuarterForPipelineCoverage ?? [],
    pipelines?.results ?? [],
    companyTarget,
    "all", // globalSelectedPipeline,
    "all", //globalSelectedOwner,
    pipelineCreatedBreakdown,
    wonBreakdown,
    managementReviewPeriodRange,
  );

  const {
    managementReviewWinLossFlows,
  } = evaluateRiskPipelineCoverage(
    hubspotManagementReviewWinLossDeals ?? [],
    currentUser?.company_id,
    wonDealsThisQuarterForPipelineCoverage ?? [],
    pipelines?.results ?? [],
    companyTarget,
    "all", // globalSelectedPipeline,
    "all", //globalSelectedOwner,
    pipelineCreatedBreakdown,
    wonBreakdown,
    managementReviewPeriodRange,
    winLossPeriodRange,
  );

  const topDealsInManagementPeriod = (hubspotManagementReviewDeals ?? [])
    .filter(deal => deal.value > 0)
    .filter(deal => isDealInManagementReviewPeriod(deal, managementReviewPeriodRange))
    .toSorted((a, b) => b.value - a.value);
  const topDealsDrivingTheQuarter = (
    isLastQuarterView
      ? topDealsInManagementPeriod.filter(deal => deal.is_closed)
      : showClosedTopDeals
        ? topDealsInManagementPeriod
        : topDealsInManagementPeriod.filter(d => !d.is_closed)
  ).slice(0, 5);
  const topDealsTitle = `Top Deals · ${getManagementReviewPeriodLabel(managementReviewPeriod)}`;
  const topDealsEmptyMessage = isLastQuarterView
    ? "No closed Deals in the selected period."
    : "No open Deals are expected to close in the selected period.";
  const showClosedTopDealsControl = !isLastQuarterView;

  const openManagementReviewUrgencyRisks = managementReviewUrgencyRisks
    .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost);
  const openManagementReviewDeals = (hubspotManagementReviewDeals ?? [])
    .filter(deal => !deal.is_closed && !deal.is_won && !deal.is_lost)
    .filter(deal => deal.value > 0);
  const closedManagementReviewDeals = (hubspotManagementReviewDeals ?? [])
    .filter(deal => deal.is_closed)
    .filter(deal => deal.value > 0);
  const largestOpenDeal = openManagementReviewDeals
    .toSorted((a, b) => b.value - a.value)[0];
  const smallestOpenDeal = openManagementReviewDeals
    .toSorted((a, b) => a.value - b.value)[0];
  const topOpenDeals = openManagementReviewDeals
    .toSorted((a, b) => b.value - a.value)
    .slice(0, 8);
  const monthPeriodRange = {
    from: startOfMonth(now),
    to: endOfMonth(now),
  };
  const finalMonthOfSelectedQuarterRange = {
    from: startOfMonth(managementReviewPeriodRange.to),
    to: managementReviewPeriodRange.to,
  };
  const firstMonthOfSelectedQuarterRange = {
    from: managementReviewPeriodRange.from,
    to: endOfMonth(managementReviewPeriodRange.from),
  };
  const keyInsightMonthPeriodRange = isLastQuarterView
    ? finalMonthOfSelectedQuarterRange
    : isNextQuarterView
      ? firstMonthOfSelectedQuarterRange
    : monthPeriodRange;
  const expectedToCloseThisMonth = canonicalForecast.getDealsForMonths(new Set([format(keyInsightMonthPeriodRange.from, "yyyy-MM")]));
  const expectedToCloseThisQuarter = canonicalForecast.getDealsForMonths(new Set(chartDataForecast
    .filter(row => !row.is_context).map(row => row.month)));
  const totalExpectedContributionThisMonth = sumExpectedContribution(expectedToCloseThisMonth);
  const totalExpectedContributionThisQuarter = sumExpectedContribution(expectedToCloseThisQuarter);
  const totalWonDealValueThisMonth = sumDealValue(expectedToCloseThisMonth.filter(deal => deal.is_won));
  const totalWonDealValueThisQuarter = sumDealValue(expectedToCloseThisQuarter.filter(deal => deal.is_won));
  const totalDealValueExpectedToCloseThisMonth = isLastQuarterView
    ? totalExpectedContributionThisMonth
    : getForecastChartValueForMonth(chartDataForecast, keyInsightMonthPeriodRange.from);
  const totalDealValueExpectedToCloseThisQuarter = isLastQuarterView
    ? totalExpectedContributionThisQuarter
    : getForecastChartValueForPeriod(chartDataForecast, managementReviewPeriodRange);
  const totalStillExpectedToCloseThisMonth = Math.max(0, totalDealValueExpectedToCloseThisMonth - totalWonDealValueThisMonth);
  const totalStillExpectedToCloseThisQuarter = Math.max(0, totalDealValueExpectedToCloseThisQuarter - totalWonDealValueThisQuarter);
  const keyInsightMonthName = format(keyInsightMonthPeriodRange.from, "MMMM");
  const monthKeyInsightTitle = isLastQuarterView
    ? `Won in ${keyInsightMonthName}`
    : isNextQuarterView
      ? `Expected to close in ${keyInsightMonthName}`
      : "Expected to close this month";
  const quarterKeyInsightTitle = isLastQuarterView
    ? "Won last quarter"
    : `Expected to close ${managementReviewPeriodPhrase}`;
  const monthKeyInsightText = isLastQuarterView
    ? `Won revenue in ${keyInsightMonthName}`
    : `${formatCurrencyValue(totalWonDealValueThisMonth, companyCurrency, 1)} won · ${formatCurrencyValue(totalStillExpectedToCloseThisMonth, companyCurrency, 1)} still expected`;
  const quarterKeyInsightText = isLastQuarterView
    ? "Won revenue in the selected period"
    : `${formatCurrencyValue(totalWonDealValueThisQuarter, companyCurrency, 1)} won · ${formatCurrencyValue(totalStillExpectedToCloseThisQuarter, companyCurrency, 1)} still expected`;
  const monthKeyInsightDescription = isLastQuarterView
    ? `Deals won in ${keyInsightMonthName}.`
    : "Deals included in the monthly expected close value. Won deals use entered deal value; open deals use weighted contribution.";
  const quarterKeyInsightDescription = isLastQuarterView
    ? "Deals won in the selected period."
    : "Deals included in the selected period. Won Deals use full value; open Deals use weighted value.";
  const periodTarget = getPeriodTarget(companyTarget, managementReviewPeriodRange);
  const targetCardTitle = isLastQuarterView
    ? "Target attainment"
    : "Pipeline to target";
  const targetCardValue = isLastQuarterView
    ? periodTarget
      ? 100 * totalWonDealValueThisQuarter / periodTarget
      : null
    : periodTarget
      ? sumTargetContribution(getExpectedToCloseDeals(hubspotManagementReviewDeals ?? [], managementReviewPeriodRange, hasPipelineStageData)) / periodTarget
      : null;
  const targetCardHeadline = targetCardValue === null
    ? "—"
    : isLastQuarterView
      ? `${targetCardValue.toFixed(0)}%`
      : `${targetCardValue.toFixed(1)}x`;
  const targetCardInfo = isLastQuarterView
    ? "Won revenue as a share of the selected period's Target."
    : "Won revenue and risk-adjusted open Deals against the selected period's Target.";
  const coverageRatioLabel = targetCardHeadline;
  const expectedToCloseNext60Days = openManagementReviewDeals
    .filter(deal => deal.adjustedCloseDate && isWithinInterval(deal.adjustedCloseDate, {
      start: now,
      end: addDays(now, 60),
    }))
    .toSorted((a, b) => {
      const dateDiff = new Date(a.adjustedCloseDate).getTime() - new Date(b.adjustedCloseDate).getTime();
      return dateDiff || b.value - a.value;
    });
  const wonDealsThisMonth = closedManagementReviewDeals
    .filter(deal => deal.is_won && deal.closed_at && isWithinInterval(new Date(deal.closed_at), {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }))
    .toSorted((a, b) => b.value - a.value);
  const wonDealsThisQuarter = closedManagementReviewDeals
    .filter(deal => deal.is_won && deal.closed_at && isWithinInterval(new Date(deal.closed_at), {
      start: managementReviewPeriodRange.from,
      end: managementReviewPeriodRange.to,
    }))
    .toSorted((a, b) => b.value - a.value);
  const lostDealsThisMonth = closedManagementReviewDeals
    .filter(deal => deal.is_lost && deal.closed_at && isWithinInterval(new Date(deal.closed_at), {
      start: startOfMonth(now),
      end: endOfMonth(now),
    }))
    .toSorted((a, b) => b.value - a.value);
  const lostDealsThisQuarter = closedManagementReviewDeals
    .filter(deal => deal.is_lost && deal.closed_at && isWithinInterval(new Date(deal.closed_at), {
      start: managementReviewPeriodRange.from,
      end: managementReviewPeriodRange.to,
    }))
    .toSorted((a, b) => b.value - a.value);
  const openPipelineValue = sumDealValue(openManagementReviewDeals);
  const averageOpenDealSize = openManagementReviewDeals.length
    ? openPipelineValue / openManagementReviewDeals.length
    : 0;
  const managementReviewChatFacts: RevenueIntelligenceChatFacts = {
    openDealCount: openManagementReviewDeals.length,
    openPipelineValueLabel: formatCurrencyValue(openPipelineValue, companyCurrency, 1),
    largestOpenDeal: largestOpenDeal
      ? formatDealFact(largestOpenDeal, companyCurrency)
      : undefined,
    smallestOpenDeal: smallestOpenDeal
      ? formatDealFact(smallestOpenDeal, companyCurrency)
      : undefined,
    topOpenDeals: topOpenDeals.map(deal => formatDealFact(deal, companyCurrency)),
    openPipelineByStage: buildStageSummary(openManagementReviewDeals, companyCurrency),
    expectedToCloseThisMonth: expectedToCloseThisMonth.map(deal => formatDealFact(deal, companyCurrency)),
    expectedToCloseThisQuarter: expectedToCloseThisQuarter.map(deal => formatDealFact(deal, companyCurrency)),
    expectedToCloseNext60Days: expectedToCloseNext60Days.map(deal => formatDealFact(deal, companyCurrency)),
    wonDealsThisMonth: wonDealsThisMonth.map(deal => formatDealFact(deal, companyCurrency)),
    wonDealsThisQuarter: wonDealsThisQuarter.map(deal => formatDealFact(deal, companyCurrency)),
    lostDealsThisMonth: lostDealsThisMonth.map(deal => formatDealFact(deal, companyCurrency)),
    lostDealsThisQuarter: lostDealsThisQuarter.map(deal => formatDealFact(deal, companyCurrency)),
    wonRevenueThisMonthLabel: formatCurrencyValue(sumDealValue(wonDealsThisMonth), companyCurrency, 1),
    wonRevenueThisQuarterLabel: formatCurrencyValue(sumDealValue(wonDealsThisQuarter), companyCurrency, 1),
    lostDealValueThisMonthLabel: formatCurrencyValue(sumDealValue(lostDealsThisMonth), companyCurrency, 1),
    lostDealValueThisQuarterLabel: formatCurrencyValue(sumDealValue(lostDealsThisQuarter), companyCurrency, 1),
    averageOpenDealSizeLabel: formatCurrencyValue(averageOpenDealSize, companyCurrency, 1),
    source: "Management Review prepared deal set using adjusted expected close date, entered deal value and approved weighted open-pipeline contribution.",
    requiredAccess: "revenue_intelligence.management_review",
    scope: {
      pageLabel: "Management Review",
      periodLabel: getManagementReviewPeriodLabel(managementReviewPeriod),
    },
  };
  const managementReviewInsightBlocks = buildManagementReviewInsightBlocks({
    coverageRatioLabel,
    isEarlyStageWeak: Boolean(isEarlyStageWeak),
    isMidStageWeak: Boolean(isMidStageWeak),
    isLateStageWeak: Boolean(isLateStageWeak),
    urgencyRiskCount: openManagementReviewUrgencyRisks.length,
  });
  const managementReviewFactsContract = defineRevenueIntelligenceAnalysisContract({
    contractId: "management_review.prepared_deal_facts",
    analysisLabel: "Management Review prepared deal facts",
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId: String(currentUser?.company_id ?? "unavailable"),
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    productLens: "The current management agenda across pipeline, expected close timing and outcomes.",
    requiredAccess: "revenue_intelligence.management_review",
    sourceHelper: "Management Review prepared deal sets",
    scope: managementReviewChatFacts.scope ?? { pageLabel: "Management Review" },
    metrics: [
      {
        metricId: "management_review.open_pipeline",
        label: "Open pipeline",
        aliases: ["open pipeline value", "pipeline total"],
        valueLabel: managementReviewChatFacts.openPipelineValueLabel,
        definition: "Entered Deal value across the prepared current open-deal population.",
        includedRecords: "Open deals in the approved Management Review prepared deal set.",
        dateField: "Current-state open status; expected-close questions use Revelate expected close date.",
        valueBasis: "Normalized entered Deal value.",
        currencyHandling: "Normalized to the company default currency before aggregation.",
        probabilityOrRiskBasis: "No probability or risk weighting is applied to the entered open-pipeline value.",
        sourceFields: ["deal.value", "deal.is_closed", "deal.is_won", "deal.is_lost"],
      },
      {
        metricId: "management_review.average_open_deal_size",
        label: "Average open deal size",
        aliases: ["average deal size", "avg open deal", "snittdeal"],
        valueLabel: managementReviewChatFacts.averageOpenDealSizeLabel,
        definition: "Open pipeline entered Deal value divided by the number of prepared open deals.",
        includedRecords: "Open deals in the approved Management Review prepared deal set.",
        dateField: "Current-state open status.",
        valueBasis: "Normalized entered Deal value.",
        currencyHandling: "Normalized to the company default currency before aggregation.",
        probabilityOrRiskBasis: "No probability or risk weighting is applied.",
        sourceFields: ["deal.value", "openDealCount"],
      },
    ],
    chat: {
      availability: "available",
      facts: managementReviewChatFacts,
      datasets: [{
        datasetId: "management_review.open_pipeline_by_stage",
        label: "Open pipeline by stage",
        description: "The same prepared open-deal population used by Management Review chat facts.",
        dimensions: [
          { field: "stage", label: "Pipeline stage", type: "category" },
        ],
        measures: [
          {
            field: "deal_value",
            label: "Open pipeline",
            metricId: "management_review.open_pipeline",
            type: "currency",
          },
          { field: "deal_count", label: "Deals", type: "number" },
        ],
        rows: managementReviewChatFacts.openPipelineByStage.map(stage => ({
          stage: stage.stageLabel,
          deal_value: stage.totalValue,
          deal_count: stage.count,
        })),
        allowedChartTypes: ["bar"],
        defaultChart: {
          type: "bar",
          title: "Open pipeline by stage",
          xField: "stage",
          yFields: ["deal_value"],
          groupBy: null,
          showConclusion: true,
        },
        sourceHelper: "buildStageSummary",
        sourceFields: ["deal.stage_label", "deal.value"],
      }],
      insights: [],
      topics: ["management review", "pipeline", "won deals", "lost deals", "expected close"],
      suggestedQuestions: [
        "What needs management attention?",
        "Which Deals close in the selected period?",
        "Show open pipeline by stage",
        "What was won this month?",
      ],
    },
  });
  const managementReviewInsightsContract = defineRevenueIntelligenceAnalysisContract({
    contractId: "management_review.executive_insights",
    analysisLabel: "Management Review executive insights",
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId: String(currentUser?.company_id ?? "unavailable"),
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    productLens: "Systematic company-level patterns, risks and management actions.",
    requiredAccess: "revenue_intelligence.management_review",
    sourceHelper: "buildManagementReviewInsightBlocks",
    scope: managementReviewChatFacts.scope ?? { pageLabel: "Management Review" },
    metrics: [],
    chat: {
      availability: "available",
      topics: ["management attention", "pipeline coverage", "forecast movement", "near-term risk"],
      suggestedQuestions: [
        "What needs management attention?",
        "Why does the first signal matter?",
        "What should we do?",
        "What is the source?",
      ],
      insights: managementReviewInsightBlocks.map(insight => ({
        insightId: insight.insightId,
        label: insight.label,
        title: insight.title,
        body: insight.body,
        evidence: insight.evidence,
        recommendedAction: insight.suggestedAction,
        sourceFields: insight.sourceFields,
        caveat: insight.caveat,
      })),
    },
  });

  return (
    <div className="space-y-4">
      <RevenueIntelligenceChatRegistration contract={managementReviewFactsContract} />
      <RevenueIntelligenceChatRegistration contract={managementReviewInsightsContract} />
      <Card className="border-none bg-transparent">
        <CardContent className="px-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row justify-between items-center gap-4">
              <h1>Management Review</h1>
              <div className="flex flex-row items-center gap-2">
                <p className="text-xs text-gray-500">
                  Period
                </p>
                <Select
                  value={managementReviewPeriod}
                  onValueChange={(value: ManagementReviewPeriod) => handleManagementReviewPeriodChange(value)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                      {managementReviewPeriodOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-col gap-y-4">
              <SectionCard>
                <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                  <div className="w-full flex flex-col gap-4">
                    {/* Header */}
                    <div className="flex flex-col gap-3">
                      <p className="font-medium text-sm">
                        Executive brief
                      </p>
                      <div className="grid gap-3 lg:grid-cols-3">
                        {managementReviewInsightBlocks.map((insight) => (
                          <div
                            key={insight.label}
                            className={cn(
                              "rounded-[8px] border px-4 py-3",
                              getInsightToneClassName(insight.tone),
                            )}
                          >
                            <div className="flex flex-row items-center justify-between gap-2 pb-2">
                              <div className="flex flex-row items-center gap-2">
                                <Sparkles className="h-3.5 w-3.5 text-[#1D819A]" />
                                <p className="text-xs font-medium text-gray-700">
                                  {insight.label}
                                </p>
                              </div>
                              <InsightSourceTooltip
                                label={insight.label}
                                source={insight.evidence}
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {insight.title}
                                </p>
                                <p className="text-sm text-gray-700">
                                  {insight.body}
                                </p>
                              </div>
                              {insight.suggestedAction && (
                                <p className="text-xs text-gray-700">
                                  Recommended action: {insight.suggestedAction}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <div className="col-span-3 pt-6">
                <div>
                  <p className="font-medium text-sm">
                    Key insights
                  </p>
                </div>
              </div>

              <div className="flex flex-col lg:grid lg:grid-cols-3 gap-x-2 gap-y-4 w-full">
                <RiskHighlightCard
                  title={monthKeyInsightTitle}
                  headline={`${formatCurrencyValue(totalDealValueExpectedToCloseThisMonth, companyCurrency, 1)}`}
                  text={monthKeyInsightText}
                  onClick={() => setKeyInsightDealList("month")}
                />
                <RiskHighlightCard
                  title={quarterKeyInsightTitle}
                  headline={`${formatCurrencyValue(totalDealValueExpectedToCloseThisQuarter, companyCurrency, 1)}`}
                  text={quarterKeyInsightText}
                  onClick={() => setKeyInsightDealList("quarter")}
                />
                <RiskHighlightCard
                  title={targetCardTitle}
                  headline={targetCardHeadline}
                  info={targetCardInfo}
                />
              </div>

              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-x-2 gap-y-4 w-full">
                <SectionCard>
                  <div className="flex min-h-[350px] flex-col bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          Revenue to plan
                        </p>
                      </div>
                    </div>

                    <RevenueVsPlanChart
                      data={chartDataForecast ?? []}
                      companyCurrency={companyCurrency}
                    />
                  </div>
                </SectionCard>
                <SectionCard>
                  <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          Historical risk-based comparison
                        </p>
                      </div>
                    </div>

                    <ForecastDriverChart
                      currentValueLabel="Risk-based estimate now"
                      data={chartDataWaterfall ?? []}
                      companyCurrency={companyCurrency}
                    />
                  </div>
                </SectionCard>
              </div>

              <div className="col-span-3 pt-6">
                <div>
                  <p className="font-medium text-sm">
                    Sales efficiency
                  </p>
                </div>
              </div>

              <div className="flex flex-col lg:grid lg:grid-cols-3 gap-x-2 gap-y-4 w-full">
                <RiskHighlightCard
                  title="Win rate"
                  headline={winRateThisQuarter === null ? "—" : `${winRateThisQuarter.toFixed(1)}%`}
                />
                <RiskHighlightCard
                  title="Average deal size"
                  headline={avgDealSizeThisQuarter === null ? "—" : `${formatCurrencyValue(avgDealSizeThisQuarter, companyCurrency, 1)}`}
                />
                <RiskHighlightCard
                  title="Pipeline created"
                  headline={`${formatCurrencyValue(Object.entries(pipelineCreatedByCategory)?.[0]?.[1]?.totalOfAllCategories ?? 0, companyCurrency, 1)}`}
                  info="Shows new pipeline created during the selected period. The chart groups created pipeline by source or segment so you can see where new demand is coming from."
                />
              </div>

              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-x-2 gap-y-4 w-full">
                <SectionCard>
                  <div className="flex h-[350px] flex-col bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          Pipeline created
                        </p>
                      </div>
                      {/* Toggle */}
                      <div className="flex flex-row gap-x-2 items-center">
                        <p className={cn("text-xs", pipelineCreatedBreakdown === "source" ? "text-black font-medium" : "text-gray-500")}>
                          Source
                        </p>
                        <Switch
                          checked={pipelineCreatedBreakdown === "segment"}
                          onCheckedChange={() => setPipelineCreatedBreakdown(prev => {
                            if (prev === "source") return "segment";
                            return "source";
                          })}
                          className="h-5 w-10"
                          thumbClassName="h-4 w-4 data-[state=checked]:translate-x-5"
                        />
                        <p className={cn("text-xs", pipelineCreatedBreakdown === "segment" ? "text-black font-medium" : "text-gray-500")}>
                          Segment
                        </p>
                      </div>
                    </div>

                    <PipelineCreatedChart
                      data={pipelineCreatedByCategory ?? {}}
                      pipelineCreatedBreakdown={pipelineCreatedBreakdown}
                      companyCurrency={companyCurrency}
                      emptyMessage={`No pipeline created yet during ${managementReviewPeriodPhrase}.`}
                    />
                  </div>
                </SectionCard>
                <SectionCard>
                  <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          Won
                        </p>
                      </div>
                      {/* Toggle */}
                      <div className="flex flex-row gap-x-2 items-center">
                        <p className={cn("text-xs", wonBreakdown === "source" ? "text-black font-medium" : "text-gray-500")}>
                          Source
                        </p>
                        <Switch
                          checked={wonBreakdown === "segment"}
                          onCheckedChange={() => setWonBreakdown(prev => {
                            if (prev === "source") return "segment";
                            return "source";
                          })}
                          className="h-5 w-10"
                          thumbClassName="h-4 w-4 data-[state=checked]:translate-x-5"
                        />
                        <p className={cn("text-xs", wonBreakdown === "segment" ? "text-black font-medium" : "text-gray-500")}>
                          Segment
                        </p>
                      </div>
                    </div>

                    <WonByCategoryChart
                      data={wonByCategory ?? {}}
                      wonBreakdown={wonBreakdown}
                      companyCurrency={companyCurrency}
                      emptyMessage={`No won Deals yet during ${managementReviewPeriodPhrase}.`}
                    />
                  </div>
                </SectionCard>
              </div>

              {/* TODO(Zabir): Pipeline movement is intentionally hidden from the current review.
                  Keep the WIP code for a later iteration once the movement data contract is reviewed. */}
              {showPipelineMovementWip && (
                <SectionCard>
                  <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      <div>
                        <p className="font-medium text-sm">
                          Pipeline movement - WIP
                        </p>
                        <p className="text-xs text-gray-500">
                          Deal value movement for the selected period, using Revelate expected close date.
                        </p>
                      </div>
                    </div>

                    <PipelineMovementChart
                      data={managementReviewPipelineMovement}
                      companyCurrency={companyCurrency}
                    />
                  </div>
                </SectionCard>
              )}

              <SectionCard>
                <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                  <div className="w-full flex flex-row justify-between items-center pb-4">
                    <div>
                      <p className="font-medium text-sm">
                        How we win and lose
                      </p>
                      <p className="text-xs text-gray-500">
                        Pipeline by created source, with lost deals split across their strongest underlying risk indicators.
                      </p>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                      <p className={cn("text-xs", winLossWindow === "selected_period" ? "font-medium text-black" : "text-gray-500")}>
                        Selected period
                      </p>
                      <Switch
                        checked={winLossWindow === "past_90_days"}
                        onCheckedChange={(checked) => setWinLossWindow(checked ? "past_90_days" : "selected_period")}
                        className="h-5 w-10"
                        thumbClassName="h-4 w-4 data-[state=checked]:translate-x-5"
                      />
                      <p className={cn("text-xs", winLossWindow === "past_90_days" ? "font-medium text-black" : "text-gray-500")}>
                        Past 90 days
                      </p>
                    </div>
                  </div>

                  <HowWeWinAndLoseChart
                    data={managementReviewWinLossFlows}
                    companyCurrency={companyCurrency}
                  />
                </div>
              </SectionCard>

              <div className="flex flex-col lg:grid lg:grid-cols-2 gap-x-2 gap-y-4 w-full">
                <SectionCard>
                  <div className="bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          {topDealsTitle}
                        </p>
                      </div>
                      {showClosedTopDealsControl && (
                        <div className="flex flex-row items-center gap-2">
                        <p className="text-xs text-gray-500">
                          Show closed
                        </p>
                        <Switch
                          checked={showClosedTopDeals}
                          onCheckedChange={setShowClosedTopDeals}
                          className="h-5 w-10"
                          thumbClassName="h-4 w-4 data-[state=checked]:translate-x-5"
                        />
                      </div>
                      )}
                    </div>

                    {/* List */}
                    <div className="flex min-h-0 flex-1 flex-col gap-y-2.5 overflow-y-auto pr-1">
                      {!topDealsDrivingTheQuarter.length && (
                        <p className="text-sm text-gray-500">
                          {topDealsEmptyMessage}
                        </p>
                      )}
                      {topDealsDrivingTheQuarter.map((d, idx) => (
                        <div key={d.id} className="flex flex-col gap-y-3">
                          <div className="flex flex-row justify-between items-center">
                            <div>
                              <p className="text-sm">
                                {d.name}
                              </p>
                              <div className="flex flex-row gap-x-1 items-center">
                                <p className="text-xs text-gray-500">
                                  {d.stage_label}
                                </p>
                                <p className="text-[8px] text-gray-500">
                                  •
                                </p>
                                <p className="text-xs text-gray-500">
                                  {d.is_closed ? "closed" : "closes"} {formatManagementReviewDealPeriodDate(d)}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-y-0 items-end">
                              <p className="text-sm">
                                {formatCurrencyValue(
                                  d.valueInDealCurrency,
                                  { code: d.currency_code } as Parameters<typeof formatCurrencyValue>[1],
                                  1,
                                )}
                              </p>
                              {d.is_won ? (
                                <p className="text-xs text-green-700">
                                  Won
                                </p>
                              ) : d.is_lost ? (
                                <p className="text-xs text-red-600">
                                  Lost
                                </p>
                              ) : (
                                <p className={cn(
                                  "text-xs capitalize",
                                  d.weighted_risk_score >= 50
                                    ? "text-red-600"
                                    : d.weighted_risk_score < 25
                                      ? "text-green-700"
                                      : "text-gray-500"
                                )}>
                                  {getRiskBucket(d.weighted_risk_score)} risk
                                </p>
                              )}
                            </div>
                          </div>
                          {idx !== topDealsDrivingTheQuarter.length - 1 && (
                            <Separator />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>
                <SectionCard>
                  <div className="flex h-[350px] flex-col bg-white border border-gray-300 rounded-[10px] py-5 px-5 shadow-sm">
                    <div className="w-full flex flex-row justify-between items-center pb-4">
                      {/* Header */}
                      <div>
                        <p className="font-medium text-sm">
                          Deal urgency
                        </p>
                        <p className="text-xs text-gray-500">
                          Near-term open deals where risk and deal value make attention most urgent.
                        </p>
                      </div>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-col">
                      <div className="grid grid-cols-[1fr_auto_68px] items-center gap-3 border-b border-gray-100 pb-2 pr-1">
                        <p className="text-xs text-gray-500">
                          Deal
                        </p>
                        <p className="text-right text-xs text-gray-500">
                          Value
                        </p>
                        <p className="text-center text-xs text-gray-500">
                          Urgency
                        </p>
                      </div>
                      <div className="flex min-h-0 flex-1 flex-col gap-y-2.5 overflow-y-auto pr-1 pt-2.5">
                      {!openManagementReviewUrgencyRisks.length && (
                        <p className="text-sm text-gray-500">
                          No urgent deals in the next 60 days.
                        </p>
                      )}
                      {openManagementReviewUrgencyRisks.map((deal, idx) => (
                        <div key={deal.id} className="flex flex-col gap-y-3">
                          <button
                            type="button"
                            className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E548E]"
                            onClick={() => setLocation(`/revenue-intelligence/deal/${deal.id}?dealDetailsViewSource=management-review`)}
                          >
                            <div>
                              <p className="text-sm">
                                {deal.name}
                              </p>
                              <div className="flex flex-row gap-x-1 items-center">
                                <p className="text-xs text-gray-500">
                                  {deal.stage_label}
                                </p>
                                <p className="text-[8px] text-gray-500">
                                  •
                                </p>
                                <p className="text-xs text-gray-500">
                                  closes {format(deal.adjustedCloseDate, "yyyy-MM-dd")}
                                </p>
                              </div>
                            </div>
                            <p className="text-right text-xs tabular-nums">
                              {formatCurrencyValue(deal.value, companyCurrency, 1)}
                            </p>
                            <DealUrgencyGauge bucket={deal.urgency_bucket} />
                          </button>
                          {idx !== openManagementReviewUrgencyRisks.length - 1 && (
                            <Separator />
                          )}
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
      <ManagementReviewDealListDialog
        companyCurrency={companyCurrency}
        open={keyInsightDealList === "month"}
        onOpenChange={open => setKeyInsightDealList(open ? "month" : null)}
        title={monthKeyInsightTitle}
        description={monthKeyInsightDescription}
        deals={expectedToCloseThisMonth}
      />
      <ManagementReviewDealListDialog
        companyCurrency={companyCurrency}
        open={keyInsightDealList === "quarter"}
        onOpenChange={open => setKeyInsightDealList(open ? "quarter" : null)}
        title={quarterKeyInsightTitle}
        description={quarterKeyInsightDescription}
        deals={expectedToCloseThisQuarter}
      />
    </div>
  );
}
